import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Badge from '@material-ui/core/Badge';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import api from '../lib/api';
import Link from './Link';
import { withRouter } from 'react-router-dom';
import { ADMIN_DRAWER_COLLAPSED_WIDTH, ADMIN_DRAWER_WIDTH } from './admin/constants';
import MaterialIcon from './admin/MaterialIcon';
import {
  MdOutlineAssessment,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineDashboard,
  MdOutlineExitToApp,
  MdOutlineHelpOutline,
  MdOutlineNotifications,
  MdOutlinePeople,
  MdOutlinePerson,
  MdOutlineReport,
  MdOutlineShoppingBasket,
  MdOutlineStadium,
  MdOutlineSupervisorAccount,
} from 'react-icons/md';
import { TbSitemap } from 'react-icons/tb';
import { LiaDumbbellSolid } from 'react-icons/lia';
import {
  SECTION_NESTED_SIDEBAR_VIEWS,
  buildSectionsNavPath,
  getSectionViewFromPathname,
  isClientsPath,
  isSectionsSidebarPath,
} from '../lib/sectionNavViews';
import { formatUserFio } from '../lib/formatUserFio';

const drawerWidth = ADMIN_DRAWER_WIDTH;
const drawerCollapsedWidth = ADMIN_DRAWER_COLLAPSED_WIDTH;

function isPlaygroundsBranch(pathname) {
  const p = pathname || '';
  return p.startsWith('/playgrounds') || p.startsWith('/playground/') || p.startsWith('/bookings');
}

class AdminSidebar extends React.Component {
  constructor(props) {
    super(props);
    const pathname = (props.location && props.location.pathname) || '';
    this.state = {
      pendingCount: 0,
      unreadNotificationsCount: 0,
      playgroundsOpen: isPlaygroundsBranch(pathname),
      sectionsOpen: isSectionsSidebarPath(pathname),
    };
  }

  componentDidMount() {
    if (this.props.userRole === 'administrator') {
      this.loadPendingCount();
      this.interval = setInterval(() => this.loadPendingCount(), 30000);
    }
    this.loadUnreadNotificationsCount();
    this.notificationsInterval = setInterval(() => this.loadUnreadNotificationsCount(), 30000);
  }

  componentDidUpdate(prevProps) {
    const prevPath = (prevProps.location && prevProps.location.pathname) || '';
    const pathname = (this.props.location && this.props.location.pathname) || '';
    if (prevPath === pathname) return;

    const next = {};
    if (isPlaygroundsBranch(pathname) && !this.state.playgroundsOpen) {
      next.playgroundsOpen = true;
    }
    if (isSectionsSidebarPath(pathname) && !this.state.sectionsOpen) {
      next.sectionsOpen = true;
    }
    if (Object.keys(next).length) this.setState(next);
  }

  componentWillUnmount() {
    if (this.interval) clearInterval(this.interval);
    if (this.notificationsInterval) clearInterval(this.notificationsInterval);
  }

  loadPendingCount = async () => {
    try {
      const count = await api.getPendingComplexesCount();
      this.setState({ pendingCount: count || 0 });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Ошибка загрузки pendingCount:', err);
    }
  };

  loadUnreadNotificationsCount = async () => {
    try {
      const data = await api.getUserNotifications();
      const unreadCount = (data && Array.isArray(data.new) ? data.new : []).length;
      this.setState({ unreadNotificationsCount: unreadCount || 0 });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Ошибка загрузки unreadNotificationsCount:', err);
    }
  };

  isActivePath = (matcher) => {
    const pathname = (this.props.location && this.props.location.pathname) || '';
    return matcher(pathname);
  };

  toggleBranch = (key) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState((prev) => ({ [key]: !prev[key] }));
  };

  wrapCollapsedTooltip = (label, node, itemKey) => {
    if (!this.props.collapsed) return node;
    const { classes } = this.props;
    return (
      <Tooltip key={itemKey} title={label} placement="right">
        <span className={classes.tooltipWrap}>{node}</span>
      </Tooltip>
    );
  };

  renderNavGroup = (label) => {
    if (this.props.collapsed) return null;
    return (
      <ListSubheader
        component="div"
        disableSticky
        className={this.props.classes.navGroup}
      >
        {label}
      </ListSubheader>
    );
  };

  renderNavItem = ({ to, label, badge, isActive, ariaLabel, nested, itemKey, icon }) => {
    const { classes, collapsed } = this.props;
    const iconNode = icon ? (
      <ListItemIcon className={classes.navIconWrap}>
        {badge && collapsed ? (
          <Badge badgeContent={badge} color="error" max={99}>
            <MaterialIcon icon={icon} className={classes.navIcon} size={20} />
          </Badge>
        ) : (
          <MaterialIcon icon={icon} className={classes.navIcon} size={20} />
        )}
      </ListItemIcon>
    ) : null;
    const content = collapsed ? null : (
      <ListItemText
        primary={
          badge ? (
            <Badge badgeContent={badge} color="error">
              <span style={{ display: 'inline-block', paddingRight: 8 }}>{label}</span>
            </Badge>
          ) : (
            label
          )
        }
      />
    );

    const item = (
      <ListItem
        key={itemKey}
        button
        dense={!!nested && !collapsed}
        component={Link}
        to={to}
        selected={!!isActive}
        aria-label={ariaLabel || label}
        className={[
          classes.navItem,
          nested && !collapsed ? classes.nestedItem : undefined,
          collapsed ? classes.navItemCollapsed : undefined,
          isActive ? classes.listItemActive : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {iconNode}
        {content}
      </ListItem>
    );

    return this.wrapCollapsedTooltip(ariaLabel || label, item, itemKey);
  };

  renderExpandableParent = ({
    to,
    label,
    isActive,
    open,
    openKey,
    onToggle,
    ariaLabel,
    itemKey,
    icon,
  }) => {
    if (this.props.collapsed) {
      return this.renderNavItem({
        to,
        label,
        icon,
        isActive,
        ariaLabel,
        itemKey,
      });
    }

    const { classes } = this.props;
    return (
      <ListItem
        key={itemKey}
        button
        component={Link}
        to={to}
        selected={!!isActive}
        aria-label={ariaLabel || label}
        aria-expanded={open}
        className={[
          classes.navItem,
          classes.parentItem,
          isActive ? classes.listItemActive : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (!open && openKey) this.setState({ [openKey]: true });
        }}
      >
        {icon ? (
          <ListItemIcon className={classes.navIconWrap}>
            <MaterialIcon icon={icon} className={classes.navIcon} size={20} />
          </ListItemIcon>
        ) : null}
        <ListItemText primary={label} />
        <span
          className={classes.chevronBtn}
          role="button"
          tabIndex={0}
          aria-label={open ? `Свернуть «${label}»` : `Развернуть «${label}»`}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onToggle(e);
          }}
        >
          {open ? (
            <span className={classes.chevronGlyph} aria-hidden="true">▴</span>
          ) : (
            <span className={classes.chevronGlyph} aria-hidden="true">▾</span>
          )}
        </span>
      </ListItem>
    );
  };

  render() {
    const { classes, userRole, userName, userLastname, collapsed, onToggleCollapsed } = this.props;
    const {
      pendingCount,
      unreadNotificationsCount,
      playgroundsOpen,
      sectionsOpen,
    } = this.state;

    const isAdmin = userRole === 'administrator';
    const isModerator = userRole === 'moderator';
    const canSeeUsers = isAdmin;
    const canSeeAdminOps = isAdmin || isModerator;
    const userFio = formatUserFio({ name: userName, lastname: userLastname });
    const identityLabel = userFio || (isAdmin ? 'Админ' : 'Модератор');

    const pathname = (this.props.location && this.props.location.pathname) || '';
    const search = (this.props.location && this.props.location.search) || '';
    const homeActive = pathname === '/';
    const sectionsActive = isSectionsSidebarPath(pathname);
    const clientsActive = isClientsPath(pathname);
    const activeSectionView = getSectionViewFromPathname(pathname);
    const centersActive = this.isActivePath((p) => p.startsWith('/centers') || p.startsWith('/center/'))
      || (isAdmin && homeActive);
    const playgroundsActive = this.isActivePath((p) => p.startsWith('/playgrounds') || p.startsWith('/playground/'));
    const bookingsActive = this.isActivePath((p) => p.startsWith('/bookings'));
    const standaloneBookingsActive = this.isActivePath((p) => p.startsWith('/standalone-bookings'));
    const notificationsActive = this.isActivePath((p) => p.startsWith('/notifications'));
    const usersActive = this.isActivePath((p) => p.startsWith('/users') || p.startsWith('/user/'));
    const staffActive = this.isActivePath((p) => p.startsWith('/organization/staff'));
    const reportsActive = this.isActivePath((p) => p.startsWith('/reports'));
    const chatReportsActive = this.isActivePath((p) => p.startsWith('/chat-reports'));
    const helpActive = this.isActivePath((p) => p.startsWith('/help'));
    const sectionsListActive = sectionsActive && activeSectionView === 'list';

    return (
      <Drawer
        variant="permanent"
        anchor="left"
        classes={{
          paper: [
            classes.drawerPaper,
            collapsed ? classes.drawerPaperCollapsed : undefined,
          ]
            .filter(Boolean)
            .join(' '),
        }}
      >
        <div className={classes.drawerHeader}>
          <div
            className={[
              classes.headerTop,
              collapsed ? classes.headerTopCollapsed : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Link
              to="/"
              className={[
                classes.brandLink,
                collapsed ? classes.brandLinkCollapsed : undefined,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label="Площадка"
              aria-current={homeActive ? 'page' : undefined}
            >
              <div
                className={[
                  classes.brandBlock,
                  collapsed ? classes.brandBlockCollapsed : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/images/logo-clean-vector.svg`}
                  alt=""
                  className={[
                    classes.logo,
                    collapsed ? classes.logoCollapsed : undefined,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
                {!collapsed ? (
                  <div className={classes.brandText}>
                    <div className={classes.brand}>Площадка</div>
                    <div className={classes.brandSub}>Панель администратора</div>
                    <div
                      className={classes.userFio}
                      title={userFio || undefined}
                    >
                      {identityLabel}
                    </div>
                  </div>
                ) : null}
              </div>
            </Link>
            <IconButton
              className={[
                classes.bellButton,
                collapsed ? classes.bellButtonCollapsed : undefined,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label="Уведомления"
              color={notificationsActive ? 'primary' : 'default'}
              onClick={() => {
                if (this.props.history) this.props.history.push('/notifications');
              }}
            >
              <Badge
                badgeContent={unreadNotificationsCount}
                color="error"
                max={99}
                invisible={!unreadNotificationsCount}
              >
                <MaterialIcon icon={MdOutlineNotifications} className={classes.bellIcon} size={20} />
              </Badge>
            </IconButton>
          </div>
        </div>

        <Divider />

        <List className={classes.list}>
          {isModerator &&
            this.renderNavItem({
              to: '/',
              label: 'Дашборд',
              icon: MdOutlineDashboard,
              isActive: homeActive,
              ariaLabel: 'Дашборд',
              itemKey: 'dashboard',
            })}
          {this.renderNavGroup('Спортивный центр')}
          {this.renderNavItem({
            to: '/centers',
            label: 'Спортивные центры',
            icon: TbSitemap,
            badge: isAdmin && pendingCount > 0 ? pendingCount : null,
            isActive: centersActive,
            ariaLabel: 'Спортивные центры',
          })}

          {this.renderExpandableParent({
            to: '/playgrounds',
            label: 'Площадки',
            icon: MdOutlineStadium,
            isActive: playgroundsActive,
            open: playgroundsOpen,
            openKey: 'playgroundsOpen',
            onToggle: this.toggleBranch('playgroundsOpen'),
            ariaLabel: 'Площадки',
            itemKey: 'playgrounds-parent',
          })}
          <Collapse in={!collapsed && playgroundsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {this.renderNavItem({
                to: '/bookings',
                label: 'Бронирования',
                isActive: bookingsActive,
                nested: true,
                ariaLabel: 'Бронирования площадок',
              })}
            </List>
          </Collapse>

          {this.renderExpandableParent({
            to: buildSectionsNavPath('list', search),
            label: 'Секции',
            icon: LiaDumbbellSolid,
            isActive: sectionsListActive,
            open: sectionsOpen,
            openKey: 'sectionsOpen',
            onToggle: this.toggleBranch('sectionsOpen'),
            ariaLabel: 'Секции',
            itemKey: 'sections-parent',
          })}
          <Collapse in={!collapsed && sectionsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {SECTION_NESTED_SIDEBAR_VIEWS.map((view) =>
                this.renderNavItem({
                  itemKey: `sec-${view.key}`,
                  to: buildSectionsNavPath(view.key, search),
                  label: view.label,
                  isActive: sectionsActive && activeSectionView === view.key,
                  nested: true,
                  ariaLabel: view.label,
                }),
              )}
            </List>
          </Collapse>

          {this.renderNavItem({
            to: buildSectionsNavPath('clients', search),
            label: 'Клиенты',
            icon: MdOutlinePeople,
            isActive: clientsActive,
            ariaLabel: 'Клиенты организации',
            itemKey: 'org-clients',
          })}
          {canSeeAdminOps &&
            this.renderNavItem({
              to: '/organization/staff',
              label: 'Сотрудники',
              icon: MdOutlineSupervisorAccount,
              isActive: staffActive,
              ariaLabel: 'Сотрудники организации',
            })}

          {canSeeAdminOps && this.renderNavGroup('Прокат')}
          {canSeeAdminOps &&
            this.renderNavItem({
              to: '/standalone-bookings',
              label: 'Доп. услуги и прокат',
              icon: MdOutlineShoppingBasket,
              isActive: standaloneBookingsActive,
              ariaLabel: 'Доп. услуги и прокат',
            })}

          {this.renderNavGroup('Система')}
          {canSeeUsers &&
            this.renderNavItem({
              to: '/users',
              label: 'Пользователи',
              icon: MdOutlinePerson,
              isActive: usersActive,
            })}
          {this.renderNavItem({
            to: '/reports',
            label: 'Отчёты',
            icon: MdOutlineAssessment,
            isActive: reportsActive,
          })}
          {isAdmin &&
            this.renderNavItem({
              to: '/chat-reports',
              label: 'Жалобы на чат',
              icon: MdOutlineReport,
              isActive: chatReportsActive,
            })}
        </List>

        <div className={classes.sidebarFooter}>
          <Divider />
          <List className={classes.list}>
            {this.renderNavItem({
              to: '/help',
              label: 'Помощь',
              icon: MdOutlineHelpOutline,
              isActive: helpActive,
              ariaLabel: 'Помощь',
              itemKey: 'help',
            })}
            {this.renderNavItem({
              to: '/logout',
              label: 'Выход',
              icon: MdOutlineExitToApp,
              isActive: false,
              itemKey: 'logout',
            })}
          </List>
        </div>

        <div className={classes.collapseFooter}>
          {this.wrapCollapsedTooltip(
            collapsed ? 'Развернуть меню' : 'Свернуть меню',
            (
              <ListItem
                button
                onClick={onToggleCollapsed}
                aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
                className={[
                  classes.collapseToggle,
                  collapsed ? classes.collapseToggleCollapsed : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <ListItemIcon className={classes.collapseIconWrap}>
                  <MaterialIcon
                    icon={collapsed ? MdOutlineChevronRight : MdOutlineChevronLeft}
                    className={classes.collapseIcon}
                    size={20}
                  />
                </ListItemIcon>
                {!collapsed ? <ListItemText primary="Свернуть" className={classes.collapseText} /> : null}
              </ListItem>
            ),
            'sidebar-collapse-toggle',
          )}
        </div>
      </Drawer>
    );
  }
}

AdminSidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  userRole: PropTypes.oneOf(['administrator', 'moderator']).isRequired,
  userName: PropTypes.string,
  userLastname: PropTypes.string,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  onToggleCollapsed: PropTypes.func,
};

AdminSidebar.defaultProps = {
  userName: '',
  userLastname: '',
  collapsed: false,
  onToggleCollapsed: () => {},
};

const styles = (theme) => ({
  drawerPaper: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowX: 'hidden',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperCollapsed: {
    width: drawerCollapsedWidth,
  },
  drawerHeader: {
    padding: theme.spacing(2.5, 2, 2),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
    minHeight: 64,
  },
  headerTopCollapsed: {
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: 0,
    paddingTop: theme.spacing(0.5),
  },
  brandLink: {
    display: 'flex',
    minWidth: 0,
    flex: 1,
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
      color: 'inherit',
    },
  },
  brandLinkCollapsed: {
    flex: '0 0 auto',
    justifyContent: 'center',
  },
  brandBlock: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: 1,
    whiteSpace: 'normal',
  },
  brandBlockCollapsed: {
    flex: '0 0 auto',
    justifyContent: 'center',
  },
  logo: {
    height: 52,
    width: 'auto',
    maxWidth: 36,
    display: 'block',
    flexShrink: 0,
    objectFit: 'contain',
    marginRight: 15,
  },
  logoCollapsed: {
    height: 36,
    maxWidth: 32,
    marginRight: 0,
  },
  brandText: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
  },
  brand: {
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: 1.2,
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
  },
  brandSub: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    whiteSpace: 'normal',
  },
  bellButton: {
    padding: 8,
    marginRight: -8,
    marginTop: 0,
    flexShrink: 0,
    alignSelf: 'center',
  },
  bellButtonCollapsed: {
    marginRight: 0,
    marginTop: theme.spacing(0.5),
  },
  bellIcon: {
    fontSize: 20,
  },
  navIconWrap: {
    minWidth: 36,
    marginRight: 0,
    color: 'inherit',
  },
  navIcon: {
    fontSize: 20,
    color: theme.palette.text.secondary,
  },
  userFio: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  list: {
    paddingTop: 0,
    paddingBottom: theme.spacing(1),
  },
  navGroup: {
    lineHeight: '28px',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.25),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'none',
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    '&:first-of-type': {
      marginTop: theme.spacing(0.5),
    },
  },
  navItem: {
    '&:hover': {
      backgroundColor: 'rgba(14, 147, 95, 0.04)',
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    '& .MuiListItemIcon-root': {
      minWidth: 0,
      justifyContent: 'center',
    },
  },
  tooltipWrap: {
    display: 'block',
    width: '100%',
  },
  parentItem: {
    marginTop: theme.spacing(0.5),
  },
  chevronBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    padding: 4,
    borderRadius: 4,
    cursor: 'pointer',
    lineHeight: 0,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(14, 147, 95, 0.08)',
    },
  },
  chevronGlyph: {
    fontSize: '0.85rem',
    lineHeight: 1,
    display: 'block',
    width: 16,
    textAlign: 'center',
  },
  listItemActive: {
    '&&': {
      backgroundColor: 'rgba(14, 147, 95, 0.08)',
      boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`,
    },
    '&&:hover': {
      backgroundColor: 'rgba(14, 147, 95, 0.12)',
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    '& $navIcon': {
      color: theme.palette.primary.main,
    },
  },
  nestedItem: {
    paddingLeft: theme.spacing(4),
  },
  sidebarFooter: {
    marginTop: 'auto',
    flexShrink: 0,
  },
  collapseFooter: {
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'rgba(30, 41, 59, 0.05)',
  },
  collapseToggle: {
    borderRadius: 0,
    color: theme.palette.text.secondary,
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    '&:hover': {
      backgroundColor: 'rgba(30, 41, 59, 0.08)',
    },
  },
  collapseToggleCollapsed: {
    justifyContent: 'center',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    '& $collapseIconWrap': {
      minWidth: 0,
      justifyContent: 'center',
    },
  },
  collapseIconWrap: {
    minWidth: 36,
    marginRight: 0,
    color: 'inherit',
  },
  collapseIcon: {
    fontSize: 20,
    color: theme.palette.text.secondary,
  },
  collapseText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  },
});

export { drawerWidth };
export default withRouter(connect(state => ({
  userName: (state.auth && state.auth.name) || '',
  userLastname: (state.auth && state.auth.lastname) || '',
}))(withStyles(styles)(AdminSidebar)));
